import * as z from "zod";
import { zCurrency } from "../currency";
import { OutboundShipmentTaxIdSchema } from "../outbound-labels/customs";
import { LandedCostProvider } from "./landed-cost-provider";

const zonosProviderInfoSchema = z.object({
  provider: z.literal(LandedCostProvider.ZONOS),
  quoteId: z.string(),
  orderId: z.string().nullish(),
});
export const ZonosProviderInfoSchema: z.ZodType<ZonosProviderInfo> =
  zonosProviderInfoSchema;
export type ZonosProviderInfo = z.infer<typeof zonosProviderInfoSchema>;

const landedCostProviderInfoSchema = z.discriminatedUnion("provider", [
  zonosProviderInfoSchema,
]);
export const LandedCostProviderInfoSchema: z.ZodType<LandedCostProviderInfo> =
  landedCostProviderInfoSchema;
export type LandedCostProviderInfo = z.infer<
  typeof landedCostProviderInfoSchema
>;

const rawLandedCostTaxIdentifierSchema = z.object({
  type: z.string(),
  value: z.string(),
});
export const RawLandedCostTaxIdentifierSchema: z.ZodType<RawLandedCostTaxIdentifier> =
  rawLandedCostTaxIdentifierSchema;
export type RawLandedCostTaxIdentifier = z.infer<
  typeof rawLandedCostTaxIdentifierSchema
>;

// Redo profit/spread is the redoPerOrderFee - providerOrderFee
export const rateLandedCostSchema = z.object({
  providerInfo: LandedCostProviderInfoSchema,
  duties: z.number(), // minor units - the actual landed cost duties that the merchant gets charged
  taxes: z.number(), // minor units - the actual landed cost taxes that the merchant gets charged
  fees: z.number(), // minor units - the actual landed cost fees that the merchant gets charged
  providerOrderFee: z.number(), // minor units — the provider's per-order fee
  redoPerOrderFee: z.number(), // minor units — Redo's per-order fee
  currency: zCurrency,
  /**
   * Zonos guarantee code for the quote this landed cost came from (e.g.
   * "POSTAL_DDP", "ZONOS", "INCLUSIVE_PRICE"). Stored as a plain string to
   * avoid coupling the model to the Zonos client enum. Read at lock time to
   * decide whether a third-party duty/tax billing account is required. Nullish
   * for legacy entries written before this field existed.
   */
  landedCostGuaranteeCode: z.string().nullish(),
});

export type RateLandedCost = z.infer<typeof rateLandedCostSchema>;

/**
 * Merchant billing snapshot for a committed LCG. Populated when the commit
 * is subject to usage-based billing (currently the outside-OMS / UPS Unlock
 * flow). Held alongside the lock so the audit-safe per-commit fee survives
 * later team-settings changes.
 *
 * - `feeCents` — audit-safe snapshot of `team.settings.landedCost.landedCostFee`
 *   at commit time
 * - `billedAt` — stamped when the commit is included in an invoice
 * - `refundPending` — set true when an already-billed commit is cancelled
 * - `refundedAt` — stamped when the credit line item is emitted
 */
const landedCostBillingSchema = z.object({
  feeCents: z.number().nullish(),
  billedAt: z.date().nullish(),
  refundPending: z.boolean().nullish(),
  refundedAt: z.date().nullish(),
});
export type LandedCostBilling = z.infer<typeof landedCostBillingSchema>;

export const LockedLandedCostSchema = rateLandedCostSchema.extend({
  taxIdentifiers: z.array(OutboundShipmentTaxIdSchema),
  // Absent for carrier-native-DDP carriers (ePostGlobal, APC) — see
  // `carrierHandlesDdpNatively`. `.nullish()` (not `.optional()`) so reads
  // tolerate already-stored `null` values in addition to `undefined`.
  thirdPartyBillingAccount: z.string().nullish(),
  billingCountryCode: z.string().nullish(),
  billingPostalCode: z.string().nullish(),
  rawResponse: z.unknown().nullish(), // Just for debugging/auditing
  billing: landedCostBillingSchema.nullish(),
  /** Commit time; window key for outside-OMS usage billing. Nullish for legacy/in-OMS entries. */
  committedAt: z.date().nullish(),
  /**
   * Shopify Fulfillment GID this commit corresponds to, when the commit was
   * keyed per-fulfillment (the outside-OMS / UPS Unlock flow). Lets the
   * commit/cancel activities target the right entry on a FulfillmentGroup
   * whose `fulfillments[]` array could in theory hold more than one entry,
   * without relying on the OMS upsert logic's 1:1 invariant.
   *
   * Nullish for backward compatibility with any pre-per-fulfillment-keying
   * entries (in-OMS lock flow does not populate this).
   */
  fulfillmentExternalId: z.string().nullish(),
});

export type LockedLandedCost = z.infer<typeof LockedLandedCostSchema>;
